import React from "react";

import { ColumnCarouselBlock, PagePreviewTileBlock } from "@reactivated";

import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { Carousel } from "../Carousel";
import { Clickable, IconButton } from "../Clickables";
import { Dots } from "../Clickables/Dots";
import { H, HLevel } from "../Headings";
import { ThemedModule } from "../ThemedModule";
import { TileImage } from "../TileImage";

import styles from "./ColumnCarousel.module.scss";

const Slide = ({ slide }: { slide: PagePreviewTileBlock[number]["value"] }) => {
    const ref = React.useRef<HTMLAnchorElement>(null);
    return (
        <article
            // eslint-disable-next-line react-hooks/refs -- delegateClickToLink defers ref.current access to the returned handler
            onClick={delegateClickToLink(ref)}
        >
            <HLevel>
                {slide.image && (
                    <TileImage value={{ ...slide, tile_type: "article" }} />
                )}
                {slide.category && (
                    <p className={styles.articleCategory}>{slide.category}</p>
                )}
                {slide.title && (
                    <H>
                        <Clickable
                            className={styles.articleTitle}
                            href={slide.url ?? undefined}
                            ref={ref}
                        >
                            {slide.title}
                        </Clickable>
                    </H>
                )}
            </HLevel>
        </article>
    );
};

export const ColumnCarousel = ({ value }: { value: ColumnCarouselBlock }) => (
    <ThemedModule theme={value.module_theme}>
        <Carousel
            containerProps={{ className: styles.carousel }}
            label={
                value.label ??
                "An unlabeled carousel is sad, but that's what we've got here. Sorry!"
            }
            controls={({ allVisible }) => (
                <div
                    className={
                        allVisible ? styles.hiddenControls : styles.controls
                    }
                >
                    <IconButton icon="caret" slot="previous" />
                    <IconButton icon="caret" slot="next" />
                </div>
            )}
            scrollContainerProps={{ className: styles.scroller }}
            dots={value.slides && <Dots slideCount={value.slides.length} />}
        >
            {value.slides?.map(({ value: slide, id }) => {
                const forcedAspectRatio =
                    value.force_aspect_ratio &&
                    value.force_aspect_ratio !== "no"
                        ? value.force_aspect_ratio
                        : null;
                return (
                    <Slide
                        slide={
                            forcedAspectRatio
                                ? {
                                      ...slide,
                                      image_aspect_ratio: forcedAspectRatio,
                                  }
                                : slide
                        }
                        key={id}
                    />
                );
            })}
        </Carousel>
    </ThemedModule>
);
